import api from './api';

export async function getMyDoctorCircleProfile() {
  const { data } = await api.get('/doctors-circle/profiles/me');
  return data?.profile || null;
}

export async function saveMyDoctorCircleProfile(payload, hasExistingProfile = false) {
  const request = hasExistingProfile
    ? api.put('/doctors-circle/profiles/me', payload)
    : api.post('/doctors-circle/profiles/me', payload);
  const { data } = await request;
  return data?.profile || null;
}

export async function uploadMyDoctorCircleProfilePhoto(file, onUploadProgress) {
  const formData = new FormData();
  formData.append('photo', file);
  const { data } = await api.post('/doctors-circle/profiles/me/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return {
    profile: data?.profile || null,
    photoUrl: data?.photoUrl || data?.profile?.profilePhotoUrl || '',
  };
}

export async function removeMyDoctorCircleProfilePhoto() {
  const { data } = await api.delete('/doctors-circle/profiles/me/photo');
  return {
    profile: data?.profile || null,
    objectDeleted: Boolean(data?.objectDeleted),
  };
}

export async function publishMyDoctorCircleProfile() {
  const { data } = await api.post('/doctors-circle/profiles/me/publish');
  return data?.profile || null;
}

export async function pauseMyDoctorCircleProfile() {
  const { data } = await api.post('/doctors-circle/profiles/me/pause');
  return data?.profile || null;
}

export async function archiveMyDoctorCircleProfile(reason) {
  const { data } = await api.post('/doctors-circle/profiles/me/archive', {
    archiveReason: reason,
  });
  return data?.profile || null;
}

export async function uploadDoctorCircleCasePhoto(file, payload, onUploadProgress) {
  const formData = new FormData();
  formData.append('photo', file);
  formData.append('photoType', payload.photoType);
  formData.append(
    'containsNoTextOrNumbersAttested',
    String(Boolean(payload.containsNoTextOrNumbersAttested))
  );
  const { data } = await api.post('/doctors-circle/profiles/me/case-photos', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
  return data?.casePhoto || null;
}

export async function archiveDoctorCircleCasePhoto(casePhotoId, reason) {
  const { data } = await api.delete(`/doctors-circle/profiles/me/case-photos/${casePhotoId}`, {
    data: { archiveReason: reason },
  });
  return data?.casePhoto || null;
}

export async function reorderDoctorCircleCasePhotos(photoOrders) {
  const { data } = await api.put('/doctors-circle/profiles/me/case-photos/reorder', {
    photoOrders,
  });
  return data?.casePhotos || [];
}

export async function updateDoctorCircleAvailability(items) {
  const { data } = await api.put('/doctors-circle/profiles/me/availability', {
    items,
  });
  return data?.availability || [];
}

export async function searchPublishedDoctorCircleProfiles(params = {}) {
  const { data } = await api.get('/doctors-circle/profiles/search', { params });
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination || { limit: 20, offset: 0, total: 0 },
    sortBy: data?.sortBy || 'relevance',
  };
}

export async function getPublishedDoctorCircleProfile(profileIdOrSlug) {
  const { data } = await api.get(`/doctors-circle/profiles/${profileIdOrSlug}`);
  return data?.profile || null;
}

export async function createDoctorCircleConsultOffer(payload) {
  const { data } = await api.post('/doctors-circle/offers', payload);
  return data?.offer || null;
}

export async function getMyDoctorCircleSentOffers(params = {}) {
  const { data } = await api.get('/doctors-circle/offers/sent', { params });
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination || { limit: 20, offset: 0, total: 0 },
  };
}

export async function getMyDoctorCircleIncomingOffers(params = {}) {
  const { data } = await api.get('/doctors-circle/offers/incoming', { params });
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination || { limit: 20, offset: 0, total: 0 },
  };
}

export async function getDoctorCircleOfferById(offerId) {
  const { data } = await api.get(`/doctors-circle/offers/${offerId}`);
  return data || null;
}

export async function markDoctorCircleOfferViewed(offerId) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/viewed`);
  return data?.offer || null;
}

export async function acceptDoctorCircleConsultOffer(offerId) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/accept`);
  return data?.offer || null;
}

export async function declineDoctorCircleConsultOffer(offerId, payload) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/decline`, payload);
  return data?.offer || null;
}

export async function getDoctorCircleUnlockFee(offerId) {
  const { data } = await api.get(`/doctors-circle/offers/${offerId}/unlock-fee`);
  return data?.fee || null;
}

export async function initiateDoctorCircleUnlockPayment(offerId) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/unlock-payment/initiate`);
  return data || null;
}

export async function getDoctorCircleUnlockPaymentStatus(offerId) {
  const { data } = await api.get(`/doctors-circle/offers/${offerId}/unlock-payment/status`);
  return data || null;
}

export async function getDoctorCircleChatThreads() {
  const { data } = await api.get('/doctors-circle/chats');
  return {
    data: Array.isArray(data?.data) ? data.data : [],
  };
}

export async function getDoctorCircleChatForOffer(offerId) {
  const { data } = await api.get(`/doctors-circle/offers/${offerId}/chat`);
  return data?.thread || null;
}

export async function openDoctorCircleChatForOffer(offerId) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/chat/open`);
  return data?.thread || null;
}

export async function getDoctorCircleChatMessages(threadId, params = {}) {
  const { data } = await api.get(`/doctors-circle/chats/${threadId}/messages`, { params });
  return {
    thread: data?.thread || null,
    data: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination || { limit: 50, offset: 0, total: 0 },
  };
}

export async function sendDoctorCircleChatMessage(threadId, messageText) {
  const { data } = await api.post(`/doctors-circle/chats/${threadId}/messages`, {
    messageText,
  });
  return data || null;
}

export async function markDoctorCircleChatThreadRead(threadId) {
  const { data } = await api.post(`/doctors-circle/chats/${threadId}/read`);
  return data?.thread || null;
}

export async function closeDoctorCircleChatThread(threadId, reason) {
  const { data } = await api.post(`/doctors-circle/chats/${threadId}/close`, {
    closeReason: reason,
  });
  return data?.thread || null;
}

export async function createDoctorCircleReport(payload) {
  const { data } = await api.post('/doctors-circle/reports', payload);
  return data?.report || null;
}

export async function getMyDoctorCircleReports(params = {}) {
  const { data } = await api.get('/doctors-circle/reports/my', { params });
  return {
    data: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination || { limit: 20, offset: 0, total: 0 },
  };
}

export async function cancelDoctorCircleConsultOffer(offerId, reason) {
  const { data } = await api.post(`/doctors-circle/offers/${offerId}/cancel`, {
    cancellationReason: reason,
  });
  return data?.offer || null;
}
